import { APP_ROUTES, APP_ROUTE_GROUPS } from '../../../constants/app-routes';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useLDClient as useLaunchDarklyClient } from 'launchdarkly-react-client-sdk';

import LEGACY_VIEWER, { SAVED_WORK_PATH } from '../../../constants/legacy-viewer';
import APP_PATHS from '../../../constants/app-paths';
import AppLoadingPage from '../../../pages/app-loading';
import LazyLayout from '../../../layouts/lazy';
import NOTIFICATION_TYPES from '../../../constants/notification-types';
import React from 'react';
import SecondaryLayout from '../../../layouts/secondary';
import getLocationWithState from '../../../utilities/routing/get-location-with-state';
import lazyDependencies from './index.lazy';
import { matchPath } from 'react-router-dom';
import usePendo from '../../../hooks/third-party/use-pendo';
import useReturnToPath from '../../../hooks/routing/use-return-to-path';
import useUserContext from '../../../hooks/context/use-user-context';

//

function isRetiredIntake(pathname) {
  return (
    LEGACY_VIEWER &&
    Boolean(
      matchPath(pathname, {
        path: [
          APP_ROUTES.addAssessment,
          APP_ROUTES.addActivityLab,
          APP_ROUTES.addSubject,
          APP_ROUTES.essayUpload,
          APP_ROUTES.createProfile,
        ],
        exact: false,
      })
    )
  );
}

function hasNoSavedProfiles(labUser) {
  return LEGACY_VIEWER && !labUser.activeStudent && !labUser.students?.length;
}

function AuthenticatedRoutes() {
  const history = useHistory();
  const labUser = useUserContext();
  const location = useLocation();
  const returnToPath = useReturnToPath();

  const { activeStudent } = useUserContext();
  const launchDarklyClient = useLaunchDarklyClient();

  //
  // Event Handlers

  function handleStudentChange() {
    history.push(returnToPath || location.state?.from || APP_PATHS.home);
  }

  //
  // Side-Effects

  usePendo(labUser);

  React.useEffect(() => {
    if (activeStudent && launchDarklyClient) {
      launchDarklyClient.identify({ key: activeStudent.uuid });
    }
  }, [launchDarklyClient, activeStudent]);

  //
  // Render

  if (labUser.loading) {
    return <AppLoadingPage />;
  }

  if (!labUser.authenticated) {
    const authTarget = new URLSearchParams(location.search).get('auth_target');
    const redirectPath =
      APP_PATHS[!LEGACY_VIEWER && authTarget === '0' ? 'createAccount' : 'login'];
    return <Redirect to={getLocationWithState(redirectPath, location)} />;
  }

  if (isRetiredIntake(location.pathname)) {
    return <Redirect to={SAVED_WORK_PATH} />;
  }

  if (hasNoSavedProfiles(labUser)) {
    return (
      <LazyLayout>
        <lazyDependencies.SavedWorkPage />
      </LazyLayout>
    );
  }

  if (
    !labUser.activeStudent &&
    ![APP_PATHS.createProfile, APP_PATHS.selectProfile].includes(location.pathname)
  ) {
    return <Redirect to={getLocationWithState(APP_PATHS.selectProfile, location)} />;
  }

  return (
    <Switch>
      {LEGACY_VIEWER && (
        <Route exact path={[SAVED_WORK_PATH, APP_ROUTES.orchestrator]}>
          <LazyLayout>
            <lazyDependencies.SavedWorkPage />
          </LazyLayout>
        </Route>
      )}
      <Route exact path={APP_ROUTES.home}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator },
            { type: NOTIFICATION_TYPES.asyncEssay },
          ]}
        >
          {LEGACY_VIEWER ? <lazyDependencies.SavedWorkPage /> : <lazyDependencies.HomePage />}
        </LazyLayout>
      </Route>

      <Route path={APP_ROUTES.addAssessment}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.AddAssessmentPage />
        </LazyLayout>
      </Route>

      <Route path={APP_ROUTES.assessments}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.AssessmentsPage />
        </LazyLayout>
      </Route>

      {LEGACY_VIEWER && (
        <Route exact path={APP_ROUTES.assignments}>
          <LazyLayout>
            <lazyDependencies.AssignmentsPage sortOrder="desc" />
          </LazyLayout>
        </Route>
      )}

      <Route path={APP_ROUTES.assignments}>
        <Switch>
          <Route exact path={APP_ROUTES.currentAssignment}>
            <LazyLayout>
              <lazyDependencies.CurrentAssignmentRedirect />
            </LazyLayout>
          </Route>

          <Route exact path={APP_ROUTE_GROUPS.assignedSkillPractice}>
            <LazyLayout layout={SecondaryLayout}>
              <lazyDependencies.AssignmentDetailPage />
            </LazyLayout>
          </Route>

          <Route path={APP_ROUTES.assignedSubject}>
            <LazyLayout>
              <lazyDependencies.AssignmentsPage sortOrder="desc" />
            </LazyLayout>
          </Route>
        </Switch>
      </Route>

      <Route path={APP_ROUTES.activityLabs}>
        <Switch>
          <Route exact path={APP_ROUTES.addActivityLab}>
            <LazyLayout
              notifications={[
                { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
              ]}
            >
              <lazyDependencies.AddActivityLabPage />
            </LazyLayout>
          </Route>

          <Route exact path={APP_ROUTES.activityLabsDetail}>
            <LazyLayout>
              <lazyDependencies.ActivityLabDetailPage />
            </LazyLayout>
          </Route>

          <Route exact path={APP_ROUTE_GROUPS.activityLabs}>
            <LazyLayout layout={SecondaryLayout}>
              <lazyDependencies.ActivityLabQuizPage />
            </LazyLayout>
          </Route>

          <Route path={APP_ROUTES.catchAll}>
            <LazyLayout>
              <Redirect to={APP_PATHS.addActivityLab} />
            </LazyLayout>
          </Route>
        </Switch>
      </Route>

      <Route exact path={APP_ROUTES.createProfile}>
        <LazyLayout layout={SecondaryLayout} withoutLabBranding>
          <lazyDependencies.CreateProfilePage onProfileCreation={handleStudentChange} />
        </LazyLayout>
      </Route>

      <Route exact path={APP_ROUTES.onDemand}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.OnDemandPage />
        </LazyLayout>
      </Route>

      <Route exact path={[APP_ROUTES.onDemandCourse, APP_ROUTES.onDemandVideo]}>
        <LazyLayout>
          <lazyDependencies.OnDemandCoursePage />
        </LazyLayout>
      </Route>

      <Route exact path={APP_ROUTES.orchestrator}>
        <LazyLayout layout={SecondaryLayout}>
          <lazyDependencies.OrchestratorPage />
        </LazyLayout>
      </Route>

      <Route exact path={APP_ROUTES.selectProfile}>
        <LazyLayout layout={SecondaryLayout} withoutLabBranding>
          <lazyDependencies.SelectProfilePage
            firstLogin={Boolean(location.state?.firstLogin)}
            onSelection={handleStudentChange}
          />
        </LazyLayout>
      </Route>

      <Route path={APP_ROUTES.sessionRecaps}>
        <LazyLayout>
          <lazyDependencies.SessionRecapsPage />
        </LazyLayout>
      </Route>

      <Route exact path={APP_ROUTES.studentAgenda}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.StudentAgendaPage />
        </LazyLayout>
      </Route>

      <Route exact path={APP_ROUTES.tutorChat}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.TutorChatPage />
        </LazyLayout>
      </Route>

      <Route path={APP_ROUTES.essays}>
        <LazyLayout
          notifications={[
            { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
          ]}
        >
          <lazyDependencies.EssayReviewsPage />
        </LazyLayout>
      </Route>

      <Route path={APP_ROUTES.subjects}>
        <Switch>
          <Route exact path={APP_ROUTES.currentSubjectAssessment}>
            <LazyLayout>
              <lazyDependencies.CurrentSubjectAssessmentRedirect />
            </LazyLayout>
          </Route>

          <Route exact path={APP_ROUTES.addSubject}>
            <LazyLayout
              notifications={[
                { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
              ]}
            >
              <lazyDependencies.AddSubjectPage />
            </LazyLayout>
          </Route>

          <Route exact path={APP_ROUTE_GROUPS.skillPractice}>
            <LazyLayout
              notifications={[
                { type: NOTIFICATION_TYPES.dayOrchestrator, options: { withoutHeading: true } },
              ]}
            >
              <lazyDependencies.SubjectDetailPage />
            </LazyLayout>
          </Route>

          <Route path={APP_ROUTES.catchAll}>
            <LazyLayout>
              <lazyDependencies.DefaultSubjectRedirect />
            </LazyLayout>
          </Route>
        </Switch>
      </Route>

      <Route path={APP_ROUTES.catchAll}>
        {/* TODO: Implement a 404 page */}
        <Redirect to={APP_PATHS.home} />
      </Route>
    </Switch>
  );
}

// Exports
// -------

export default AuthenticatedRoutes;
